import { Auth0Provider, useAuth0 } from '@auth0/auth0-react'
import { QueryClientProvider } from '@tanstack/react-query'
import type { History } from 'history'
import { useState } from 'react'
import { BrowserRouter, Redirect, Route, Router, Switch } from 'react-router-dom'
import { queryClient } from '~/api/query-client'
import ServerReadinessScreen from '~/components/ServerReadinessScreen'
import {
  getUserEmail,
  getUserIsAdmin,
  getUserName,
  getUserOrgName,
  getUserPicture,
} from '~/core/appContextUtils'
import { DemoDataProvider } from '~/demoControls/DemoContext'
import DemoControlsPage from '~/demoControls/DemoControlsPage'
import AuthTokenDashboard from '~/pages/AuthTokenDashboard'
import Cle2025Demo from '~/pages/Cle2025/Cle2025Demo'
import CleDemo from '~/pages/CleDemo'
import CulturesPage from '~/pages/cultures/CulturesPage'
import IntegrationsDashboard from '~/pages/IntegrationsDashboard'
import LocalInstrumentsDashboard from '~/pages/LocalInstrumentsDashboard'
import { CustomCultureStatusProvider } from '~/pages/Monitor/contexts/CustomCultureStatusContext'
import MonitorRoutes from '~/pages/Monitor/pages'
import NoOrg from '~/pages/NoOrg'
import CloudSettings from '~/pages/OnPremAgent/CloudSettings/CloudSettings'
import DataIngestorRoutes from '~/pages/OnPremAgent/DataIngestors/DataIngestorRoutes'
import DatasetRoutes from '~/pages/OnPremAgent/Datasets/DatasetRoutes'
import OrganoidDemo from '~/pages/OrganoidDemo/OrganoidDemo'
import PlateRoutes from '~/pages/plate/PlateRoutes'
import SettingsPage from '~/pages/Settings/SettingsPage'
import CommandCenter from '~/pages/slasDemo/CommandCenter'
import UiPlayground from '~/pages/UiPlayground'
import Workcell from '~/pages/Workcell'
import { parseStringAsBool } from '~/utils/string'
import type { AppContext } from '../AppContext'
import Diagnostics from '../Diagnostics'
import { FeatureFlagProvider } from '../featureFlags'
import LeftNav from '../LeftNav'
import {
  type MultiFrontendContext,
  USER_ADMIN_ROLE,
  UserMetadataProvider,
} from '../MultiFrontendContext'
import UpdateNotification from '../UpdateNotification'
import cs from './app.scss'
import getAuth0LoginURLParams, { EXTERNAL_LOGIN_PATH } from './getAuth0LoginURLParams'
import LoginPage from './LoginPage'
import makeFrontendContextFromAuth0User from './makeFrontendContextFromAuth0User'
import useTelemetryTags from './useTelemetryTags'

const SignedInApp = ({
  frontendContext,
  history,
}: {
  frontendContext: MultiFrontendContext
  history: History
}) => {
  const homePage = frontendContext.homePage || '/no-org'
  // Note: For now, we only support the concept of enabled integrations in the automation platform.
  // This may extend to include cloud-specific integrations in the near-future.
  const enabledIntegrations = frontendContext.orgMetadata.automationEnabledIntegrations
  const demoUseLocalAssets = frontendContext.demoUseLocalAssets

  const appContent = (
    <UserMetadataProvider value={frontendContext.userMetadata}>
      <div className={cs.app}>
        <Router history={history}>
          <CustomCultureStatusProvider>
            <LeftNav frontendContext={frontendContext} />
            <UpdateNotification />
            <div className={cs.main}>
              <Switch>
                <Route exact path='/' render={() => <Redirect to={homePage} />} />
                <Route path={EXTERNAL_LOGIN_PATH} render={() => <LoginPage />} />
                <Route path='/workcell' render={() => <Workcell />} />
                <Route
                  path='/command-center'
                  render={() => <CommandCenter useLocalAssets={demoUseLocalAssets} />}
                />
                <Route
                  path='/monitor'
                  render={() => <MonitorRoutes frontendContext={frontendContext} />}
                />
                <Route path='/plate' render={() => <PlateRoutes />} />
                <Route path='/cultures' render={() => <CulturesPage />} />
                <Route
                  path='/cle-2025'
                  render={() => <Cle2025Demo useLocalAssets={demoUseLocalAssets} />}
                />
                <Route
                  path='/cell-line-engineering'
                  render={() => <CleDemo useLocalAssets={demoUseLocalAssets} />}
                />
                <Route
                  path='/organoids'
                  render={() => <OrganoidDemo useLocalAssets={demoUseLocalAssets} />}
                />
                <Route path='/settings' render={() => <SettingsPage />} />
                <Route path='/cloud-settings' render={() => <CloudSettings />} />
                <Route
                  path='/integrations'
                  render={() => (
                    <IntegrationsDashboard
                      enabledIntegrations={enabledIntegrations}
                      userMetadata={frontendContext.userMetadata}
                    />
                  )}
                />
                <Route path='/auth-tokens' render={() => <AuthTokenDashboard />} />
                <Route path='/ui' render={() => <UiPlayground />} />
                <Route path='/no-org' render={() => <NoOrg />} />
                <Route
                  path='/local-instruments'
                  render={() => <LocalInstrumentsDashboard />}
                />
                <Route path='/demo-controls' render={() => <DemoControlsPage />} />
                <Route path='/diagnostics' render={() => <Diagnostics />} />
                <Route
                  path='/data-ingestors'
                  render={() => (
                    <DataIngestorRoutes frontendContext={frontendContext} />
                  )}
                />
                <Route
                  path='/datasets'
                  render={() => <DatasetRoutes frontendContext={frontendContext} />}
                />
              </Switch>
            </div>
          </CustomCultureStatusProvider>
        </Router>
      </div>
    </UserMetadataProvider>
  )

  // For on-prem agent deployment, wrap with server readiness check
  if (frontendContext.deploymentMode === 'on_prem_agent') {
    return (
      <ServerReadinessScreen
        serverUrl={process.env.MONOMER_ON_PREM_AGENT_ORIGIN || 'http://localhost:8090'}
      >
        {appContent}
      </ServerReadinessScreen>
    )
  }

  return appContent
}

const OuterApp = ({ history }: { history: History }) => {
  if (window.appContext) {
    // App served from Django

    // It is safe to call a hook in this conditional because the presence of
    // window.appContext will never change during a session.
    const [frontendContext, _setFrontendContext] = useState<MultiFrontendContext>(
      makeInitialFrontendContextFromLegacyAppContext(window.appContext),
    )

    return (
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SignedInApp frontendContext={frontendContext} history={history} />
        </BrowserRouter>
      </QueryClientProvider>
    )
  }

  // Single-page app
  const { invitation, organization } = getAuth0LoginURLParams()
  return (
    <Auth0Provider
      domain={process.env.AUTH0_CLIENT_DOMAIN!}
      clientId={process.env.AUTH0_CLIENT_ID!}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: process.env.MONOMER_MONITOR_JWT_AUDIENCE,
        invitation: invitation,
        organization: organization,
      }}
      useRefreshTokens
      cacheLocation='localstorage'
    >
      <QueryClientProvider client={queryClient}>
        <FeatureFlagProvider>
          <DemoDataProvider>
            <BrowserRouter>
              <Auth0ClientApp history={history} />
            </BrowserRouter>
          </DemoDataProvider>
        </FeatureFlagProvider>
      </QueryClientProvider>
    </Auth0Provider>
  )
}

const Auth0ClientApp = ({ history }: { history: History }) => {
  const { isAuthenticated, user } = useAuth0()
  useTelemetryTags(user)

  if (!isAuthenticated || !user) {
    return <LoginPage />
  }
  const frontendContext = makeFrontendContextFromAuth0User(user)

  return <SignedInApp frontendContext={frontendContext} history={history} />
}

function makeInitialFrontendContextFromLegacyAppContext(
  appContext: AppContext,
): MultiFrontendContext {
  return {
    deploymentMode: 'automation',
    cloudFrontend: process.env.MONOMER_CLOUD_FRONTEND_AVAILABLE_AT,
    homePage: '/workcell',
    monitorEnabled: false,
    useCoreAuth: true,
    demoUseLocalAssets: parseStringAsBool(
      process.env.MONOMER_USE_LOCAL_ASSETS_FOR_DEMO,
    ),
    orgMetadata: {
      // Note: Historically, the slack integration has always been enabled by default
      automationEnabledIntegrations: appContext.organization_config
        .enabled_integrations || ['slack'],
      // Note: Historically, we default to showing all pages if enabled_pages is not defined
      enabledPages: appContext.organization_config.enabled_pages || 'all',
    },
    userMetadata: {
      name: getUserName(appContext),
      picture: getUserPicture(appContext),
      email: getUserEmail(appContext),
      orgDisplayName: getUserOrgName(appContext),
      roles: getUserIsAdmin(appContext) ? [USER_ADMIN_ROLE] : [],
    },
  }
}

export default OuterApp
